import React from "react";
import { Link, useLocation } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";
import DynamicTitle from "./DynamicTitle";
import WaveDivider from "./WaveDivider";

function WhatIsBilateralStimulation() {
  const location = useLocation();
  return (
    <div className="flex flex-col min-h-screen">
      <DynamicTitle>What is Bilateral Stimulation?</DynamicTitle>
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-surfaceTint py-16">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-text">
              What is Bilateral Stimulation?
            </h1>
            <p className="text-xl text-textSecondary">
              A simple, research-backed technique that helps you take the edge off stress and find calm.
            </p>
          </div>
        </section>

        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Section 1: What IS bilateral stimulation */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              The Simple Explanation
            </h2>
            <p className="text-lg mb-4 text-textSecondary">
              Bilateral stimulation is any sensory input that alternates between the left and right
              sides of your body. The most common forms include:
            </p>
            <ul className="list-disc list-inside text-lg mb-6 text-textSecondary space-y-2 ml-4">
              <li><strong>Visual:</strong> Watching something move back and forth (like a dot or light)</li>
              <li><strong>Auditory:</strong> Hearing tones that alternate between your left and right ear</li>
              <li><strong>Tactile:</strong> Feeling alternating taps on your left and right hands or knees</li>
            </ul>
            <div className="bg-surfaceTint border-l-4 border-secondary p-4 rounded">
              <p className="text-text">
                <strong>In our sessions:</strong> You follow a moving dot with your eyes while optionally
                listening to alternating audio tones. It's simple, gentle, and you control the pace.
              </p>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Section 2: How does it work */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              How Does It Work?
            </h2>
            <p className="text-lg mb-4 text-textSecondary">
              While researchers are still exploring the exact mechanisms, here's what we know:
            </p>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-tint">
                <h3 className="font-semibold text-text mb-2">Engages Both Sides of Your Brain</h3>
                <p className="text-textSecondary">
                  The left-right pattern activates both hemispheres of your brain, similar to what
                  happens naturally during REM sleep—when your brain processes the day's experiences.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-tint">
                <h3 className="font-semibold text-text mb-2">Takes the Edge Off</h3>
                <p className="text-textSecondary">
                  Studies show bilateral stimulation can soften how intense or vivid a sticky thought
                  or memory feels, so it has less of a grip on you.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-tint">
                <h3 className="font-semibold text-text mb-2">Helps You Settle</h3>
                <p className="text-textSecondary">
                  The rhythmic, predictable pattern helps your body shift from stressed and revved up
                  toward a calmer, steadier state.
                </p>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Section 3: What does it feel like */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              What Does It Feel Like?
            </h2>
            <p className="text-lg mb-4 text-textSecondary">
              Everyone's experience is different, but here's what many people report:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-surfaceTint p-4 rounded-lg">
                <p className="text-text">
                  <strong>"The weight lifted"</strong> — A sense of relief or lightness after a tough day
                </p>
              </div>
              <div className="bg-surfaceTint p-4 rounded-lg">
                <p className="text-text">
                  <strong>"It felt distant"</strong> — Stuck thoughts feel less intense, like
                  they're not running the show
                </p>
              </div>
              <div className="bg-surfaceTint p-4 rounded-lg">
                <p className="text-text">
                  <strong>"I felt calmer"</strong> — Less wound up, more settled
                </p>
              </div>
              <div className="bg-surfaceTint p-4 rounded-lg">
                <p className="text-text">
                  <strong>"Things made sense"</strong> — A new angle on something that had been on your mind
                </p>
              </div>
            </div>
            <p className="text-textSecondary text-sm italic">
              Note: It's also normal to feel nothing dramatic at first, or to have feelings come up
              during a session. Both responses are okay.
            </p>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="surfaceTint" />

        {/* Section 4: What is it used for */}
        <section className="pt-8 pb-12 bg-surfaceTint">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              What Can You Use It For?
            </h2>
            <p className="text-lg mb-4 text-textSecondary">
              People use bilateral stimulation sessions for:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <ul className="space-y-3 text-textSecondary">
                <li className="flex items-start">
                  <span className="text-accent mr-2">•</span>
                  Taking the edge off everyday stress
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">•</span>
                  Quieting a racing or anxious mind
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">•</span>
                  Settling down when something is stuck on your mind
                </li>
              </ul>
              <ul className="space-y-3 text-textSecondary">
                <li className="flex items-start">
                  <span className="text-accent mr-2">•</span>
                  Calming down after a hard day
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">•</span>
                  Finding focus and clarity
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">•</span>
                  Rounding out an existing wellness routine
                </li>
              </ul>
            </div>
          </div>
        </section>

        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Section 5: Is it the same as EMDR */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              Is This the Same as EMDR?
            </h2>
            <p className="text-lg mb-4 text-textSecondary">
              Not quite. Here's the distinction:
            </p>
            <div className="bg-surfaceTint p-6 rounded-lg mb-4">
              <p className="text-textSecondary mb-4">
                <strong>EMDR (Eye Movement Desensitization and Reprocessing)</strong> is a specific
                therapeutic protocol that uses bilateral stimulation as one component. EMDR is a
                trademarked clinical approach that should only be administered by trained,
                licensed therapists.
              </p>
              <p className="text-textSecondary">
                <strong>RealignMind</strong> uses bilateral stimulation techniques for wellness and
                self-regulation. Our sessions are inspired by the research behind EMDR, but we are
                <em> not</em> EMDR therapy and do not replace professional treatment.
              </p>
            </div>
            <p className="text-textSecondary text-sm">
              Think of it like this: EMDR is a comprehensive protocol led by a therapist. Our sessions
              are a self-directed wellness tool that uses one component (bilateral stimulation) from
              that research.
            </p>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="surfaceTint" />

        {/* Section 6: Is there research */}
        <section className="pt-8 pb-12 bg-surfaceTint">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              Is There Research Behind This?
            </h2>
            <p className="text-lg mb-4 text-textSecondary">
              Yes. Bilateral stimulation has been studied for decades, with research published by
              major institutions including:
            </p>
            <ul className="list-disc list-inside text-lg mb-6 text-textSecondary space-y-1 ml-4">
              <li>World Health Organization (WHO)</li>
              <li>American Psychiatric Association (APA)</li>
              <li>U.S. Department of Veterans Affairs (VA)</li>
            </ul>
            <p className="text-lg mb-4 text-textSecondary">
              Key findings include studies showing bilateral stimulation can help reduce stress and
              soften how intense difficult memories feel.
            </p>
            <Link
              to="/how-it-works"
              className="inline-block bg-accent text-white font-semibold py-2 px-4 rounded-lg hover:bg-accentDark transition-colors"
            >
              Explore the Research →
            </Link>
          </div>
        </section>

        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Section 7: Is it safe */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-6 text-text">
              Is It Safe?
            </h2>
            <p className="text-lg mb-4 text-textSecondary">
              Bilateral stimulation is generally considered safe for most people. However:
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-start">
                <span className="mr-3 text-xl">⚠️</span>
                <p className="text-textSecondary">
                  <strong>Not recommended during active crisis.</strong> If you're in acute distress,
                  please reach out to a crisis resource first.
                </p>
              </div>
              <div className="flex items-start">
                <span className="mr-3 text-xl">⚠️</span>
                <p className="text-textSecondary">
                  <strong>Not a replacement for professional care.</strong> Our sessions are a wellness
                  tool, not therapy. If you have serious mental health concerns, please work with a
                  licensed professional.
                </p>
              </div>
              <div className="flex items-start">
                <span className="mr-3 text-xl">⚠️</span>
                <p className="text-textSecondary">
                  <strong>If you're working with a therapist,</strong> consider checking in with them
                  before adding self-directed bilateral stimulation to your routine.
                </p>
              </div>
            </div>
            <Link
              to="/emergency-support"
              className="text-accent hover:underline font-medium"
            >
              View crisis resources →
            </Link>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="primary" />

        {/* CTA Section — keeps the one allowed gradient */}
        <section className="pt-8 pb-12 bg-gradient-to-br from-accent to-primary">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">
              Ready to Try It?
            </h2>
            <p className="text-lg mb-8 text-white/90">
              Experience bilateral stimulation for yourself with our interactive demo—no
              signup required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/demo"
                state={{ from: location.pathname }}
                className="inline-block bg-white text-accent font-semibold py-3 px-6 rounded-full hover:bg-surfaceTint transition-colors"
              >
                Try the Demo
              </Link>
              <Link
                to="/subscribe"
                className="inline-block bg-transparent border border-white text-white font-semibold py-3 px-6 rounded-full hover:bg-white hover:text-accent transition-colors"
              >
                Start Your Free Trial
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default WhatIsBilateralStimulation;
